import * as React from 'react';
import {connect} from 'react-redux';
import {Route, Redirect} from 'react-router-dom';

import * as ramda from 'ramda';

import {LAST_365_DAYS} from 'nutshell-core/constants';
import {deserializeFieldNames} from 'nutshell-core/entities/deserialize-fields';
import {convertToArray} from 'nutshell-core/filter';
import type {FilterObject} from 'nutshell-core/types';

import {Select} from 'shells/select/select';

import type {NutshellState} from '../../../store';

import {AiGeneratedBanner} from '../banner/ai-generated-banner';
import {makeHandleClickChartToZoom} from '../charts/make-handle-click-chart-to-zoom';
import {LeadsAndActivitiesReportProvider} from '../leads-and-activities-report-provider';
import {ReportChart} from '../report-chart';
import {
    getReportDocumentTitle,
    getReportDefaultState,
    getReportDefaultFields,
    CUSTOM,
    GAP_SELECT_VALUES_DAY_TO_YEAR,
} from '../report-constants';
import {ReportDataSummary} from '../report-data-summary';
import type {
    LeadsReportKey,
    ChartReportParams,
    DateRangeType,
    ReportURLParams,
} from '../report-types';
import * as actions from '../reports-actions';
import * as reportSelectors from '../reports-selectors';

import {DetailViewContainer} from './detail-view-container';
import {QuotasViaReportsCtaBanner} from './quotas-via-reports-cta';
import {ReportFeatureGatePageWrapper} from './report-feature-gate-page-wrapper';

import styles from './leads-report.module.css';

const DEFAULT_CUSTOM_REPORT_DATE_RANGE = LAST_365_DAYS.id;

type OwnProps = {
    reportKey: LeadsReportKey;
    activeFilters: Array<FilterObject>;
    onParamChange: (arg1: any, arg2: boolean) => any;
    onParamPush: (arg1: any, arg2: boolean) => any;
    urlParams: ReportURLParams<'leads'>;
    location: any;
};
type StateProps = {
    isFilterDrawerOpen: boolean;
    chartBucketCount: number;
};
type DispatchProps = {
    fetchReportData: (arg1: LeadsReportKey, arg2: ChartReportParams, arg3: Array<any>) => any;
};
type Props = OwnProps & StateProps & DispatchProps;

function mapStateToProps(state: NutshellState): StateProps {
    return {
        isFilterDrawerOpen: reportSelectors.getIsFilterDrawerOpen(state),
        chartBucketCount: reportSelectors.getCurrentReportChartBucketCount(state),
    };
}

// @ts-expect-error
function mapDispatchToProps(dispatch) {
    return {
        // @ts-expect-error
        fetchReportData: (reportKey, reportParams, filters) => {
            dispatch(actions.requestLeadsReportData({reportKey, reportParams, filters}));
        },
    };
}

export class LeadsReportComponent extends React.PureComponent<Props> {
    static defaultProps = {
        urlParams: {
            report: {},
            filter: {},
        },
    };

    componentDidMount() {
        const reportKey = this.props.reportKey;
        const reportParams = this.props.urlParams.report;
        const filters = convertToArray(this.props.urlParams.filter);
        this.props.fetchReportData(reportKey, reportParams, filters);
        document.title = getReportDocumentTitle(this.props.reportKey);
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        const {
            reportKey: nextReportKey,
            urlParams: {report: nextReportParams, filter: nextFilterParams},
        } = nextProps;

        const {
            reportKey,
            urlParams: {report: reportParams, filter: filterParams},
        } = this.props;

        const isSegmented = nextReportParams.segment !== undefined;
        const reportKeyChanged = nextReportKey !== reportKey;
        const filtersChanged = !ramda.equals(nextFilterParams, filterParams);
        const gapChanged = nextReportParams.gap !== reportParams.gap;
        const segmentChanged = nextReportParams.segment !== reportParams.segment;
        const compareToChanged = nextReportParams.compareTo !== reportParams.compareTo;

        const universalPropsChanged = reportKeyChanged || filtersChanged;

        if (
            gapChanged ||
            (compareToChanged && nextReportParams.compareTo) ||
            (isSegmented && segmentChanged) ||
            universalPropsChanged
        ) {
            const nextFilters = convertToArray(nextFilterParams);
            this.props.fetchReportData(nextReportKey, nextReportParams, nextFilters);
        }
    }

    render() {
        const reportKey = this.props.reportKey;
        if (
            reportKey === 'sales' ||
            reportKey === 'newleads' ||
            NutClientConfig.hasOnDemandReporting
        ) {
            return (
                <Route path={'/reports/:report/:tab'}>
                    {({match, location}) => {
                        const {search: defaultQuery} = getReportDefaultState(this.props.reportKey);
                        const reportParams = this.props.urlParams.report || {};
                        const filterParams = this.props.urlParams.filter || {};
                        const listParams = this.props.urlParams.list || {};
                        let queryString = location.search;

                        if (!queryString) {
                            queryString = defaultQuery;

                            return <Redirect to={`${location.pathname}${queryString}`} />;
                        }

                        const currentGapIndex = GAP_SELECT_VALUES_DAY_TO_YEAR.findIndex((gap) => {
                            return gap.id === reportParams.gap;
                        });

                        const isFiltering = this.props.isFilterDrawerOpen;

                        const selectedFields =
                            // @ts-expect-error
                            listParams.fields && listParams.fields.leads
                                ? // @ts-expect-error
                                  deserializeFieldNames(listParams.fields.leads)
                                : deserializeFieldNames(
                                      getReportDefaultFields(this.props.reportKey)
                                  );

                        let xAxisLabel;
                        if (this.props.reportKey === CUSTOM) {
                            xAxisLabel = (
                                <LeadBinSelect
                                    dateRangeType={reportParams.dateRangeType}
                                    onDateRangeTypeChange={this.handleDateRangeTypeChange}
                                />
                            );
                        } else {
                            xAxisLabel =
                                reportParams.dateRangeType === 'closedTime'
                                    ? 'Lead close date'
                                    : 'Lead open date';
                        }

                        // Some summary information about the leads being charted
                        const reportDataSummary = (
                            <ReportDataSummary
                                currentGapIndex={currentGapIndex}
                                filterParams={filterParams}
                                reportParams={reportParams}
                                reportKey={this.props.reportKey}
                            />
                        );

                        return (
                            <div
                                id='report-content-scroll-container'
                                className={
                                    isFiltering ? styles['container--narrow'] : styles.container
                                }
                            >
                                {(this.props.reportKey === 'sales' ||
                                    this.props.reportKey === 'forecast') &&
                                    NutClientConfig.hasQuotasV2FF && <QuotasViaReportsCtaBanner />}
                                {this.props.urlParams.report.id && (
                                    <div className='px-16'>
                                        <AiGeneratedBanner
                                            reportId={this.props.urlParams.report.id}
                                        />
                                    </div>
                                )}
                                <LeadsAndActivitiesReportProvider>
                                    <div className={styles['report-chart-container']}>
                                        <ReportChart
                                            useBuiltInEmptyState={true}
                                            activeFilters={this.props.activeFilters}
                                            // @ts-expect-error
                                            onChartClick={makeHandleClickChartToZoom(
                                                currentGapIndex,
                                                this.props.chartBucketCount,
                                                this.props.onParamPush,
                                                this.props.urlParams.report.dateRangeType
                                            )}
                                            onParamChange={this.props.onParamChange}
                                            reportDataSummary={reportDataSummary}
                                            reportKey={this.props.reportKey}
                                            reportParams={reportParams}
                                            xAxisLabel={xAxisLabel}
                                        />
                                    </div>
                                </LeadsAndActivitiesReportProvider>
                                <DetailViewContainer
                                    reportKey={this.props.reportKey}
                                    // @ts-expect-error
                                    selectedTab={match.params.tab || 'details'}
                                    location={this.props.location}
                                    filters={this.props.activeFilters}
                                    facet={reportParams.facet}
                                    segmentId={reportParams.segment}
                                    selectedPage={
                                        // @ts-expect-error
                                        listParams.page && Number(listParams.page)
                                            ? // @ts-expect-error
                                              Number(listParams.page)
                                            : 1
                                    }
                                    // @ts-expect-error
                                    selectedSortField={listParams.sort}
                                    selectedFields={selectedFields}
                                    onParamChange={this.props.onParamChange}
                                />
                            </div>
                        );
                    }}
                </Route>
            );
        } else {
            return <ReportFeatureGatePageWrapper reportKey={reportKey} />;
        }
    }

    handleDateRangeTypeChange = (dateRangeType: DateRangeType) => {
        const oldFilters = this.props.urlParams.filter;
        const oldDateRangeType = this.props.urlParams.report.dateRangeType;
        const oldLeadBin = dateRangeType === 'createdTime' ? 'closedTime' : 'createdTime';
        const newDateRange = oldFilters[oldLeadBin] || DEFAULT_CUSTOM_REPORT_DATE_RANGE;
        const newParams = {
            report: {dateRangeType},
            filter: {...oldFilters, [oldDateRangeType]: undefined, [dateRangeType]: newDateRange},
        } as const;
        this.props.onParamChange(newParams, true);
    };
}

// @ts-expect-error
const connector = connect<Props, OwnProps, _, _, _, _>(mapStateToProps, mapDispatchToProps);

// @ts-expect-error
export const LeadsReport = connector(LeadsReportComponent);

const LEAD_BIN_TIME_SELECT_VALUES = [
    {value: 'createdTime', label: 'Lead open date'},
    {value: 'closedTime', label: 'Lead closed date'},
];

type DateRangeTypeSelectProps = {
    dateRangeType: DateRangeType;
    onDateRangeTypeChange: (arg1: DateRangeType) => any;
};
const LeadBinSelect = ({dateRangeType, onDateRangeTypeChange}: DateRangeTypeSelectProps) => {
    const selectedOption = LEAD_BIN_TIME_SELECT_VALUES.find((opt) => opt.value === dateRangeType);

    return (
        <Select
            styleOptions={{filled: false}}
            value={selectedOption}
            valueKey='value'
            labelKey='label'
            selectValues={LEAD_BIN_TIME_SELECT_VALUES}
            onChange={(opt) => onDateRangeTypeChange(opt.value)}
        />
    );
};
